import { FC, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';
import { useNavigationData } from '../modules/NavigationProvider';
import { usePolesContext } from '../modules/poles/PolesContext';
import ToggleSwitch from '../components/ToggleSwitch';
import ColorPicker from '../components/ColorPicker';
import IconSVG from '../components/IconSVG';

const LightsPage: FC = () => {
  const { t } = useTranslation('lights');
  const navigate = useNavigate();
  const { setPath, setButtonText, setHeaderText } = useNavigationData();
  const { currentPole, onChangeRGB, onChangeWhite, onChangeLedProfile } =
    usePolesContext();

  const rgbSwitch = useMemo(
    () => currentPole?.firmware?.ledrgb?.state ?? false,
    [currentPole?.firmware?.ledrgb?.state]
  );
  const lightSwitch = useMemo(
    () => currentPole?.firmware?.ledw?.state ?? false,
    [currentPole?.firmware?.ledw?.state]
  );
  const scheduleSwitch = useMemo(
    () => currentPole?.firmware?.ledschedule?.type === 1,
    [currentPole?.firmware?.ledschedule?.type]
  );
  const sunSwitch = useMemo(
    () => currentPole?.firmware?.ledschedule?.type === 2,
    [currentPole?.firmware?.ledschedule?.type]
  );

  const timerRefSun = useRef<number | null>(null);
  const timerRefColor = useRef<number | null>(null);

  const [colorSlider, setColorSlider] = useState<string>(
    currentPole?.firmware?.ledrgb?.color ?? '#000000'
  );

  useLayoutEffect(() => {
    setPath('/menu');
    setButtonText();
    setHeaderText(t('title'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    setColorSlider(currentPole?.firmware?.ledrgb?.color ?? '#000000');
  }, [currentPole?.firmware?.ledrgb?.color]);

  const handleRGBSwitch = () => {
    // Send RGB LED state to API
    onChangeRGB({ state: !rgbSwitch });
  };

  const handleLightSwitch = () => {
    // Send White LED state to API
    onChangeWhite({ state: !lightSwitch });
  };

  const handleColorSlider = (hex: string) => {
    if (timerRefColor.current !== null) {
      clearTimeout(timerRefColor.current);
    }

    setColorSlider(hex);

    // Send RGB LED color to API
    timerRefColor.current = window.setTimeout(() => {
      onChangeRGB({ color: hex });
    }, 2000);
  };

  const handleSunSwitch = () => {
    if (timerRefSun.current !== null) {
      clearTimeout(timerRefSun.current);
    }

    const newValue = !sunSwitch;

    // Send new profile to API
    timerRefSun.current = window.setTimeout(() => {
      onChangeLedProfile({ type: newValue ? 2 : 0 });
    }, 2000);
  };

  return (
    <>
      <div className="d-flex justify-content-between">
        <div className="d-flex flex-column align-items-start text-start gap-2">
          {currentPole?.firmware.ledrgb.allowed && (
            <ToggleSwitch
              variant="label"
              label={t('rgb')}
              checked={rgbSwitch}
              onChange={handleRGBSwitch}
            >
              <span className="text-uppercase ms-2">
                {t(rgbSwitch ? 'off' : 'on')}
              </span>
            </ToggleSwitch>
          )}
          {currentPole?.firmware.ledw.allowed && (
            <ToggleSwitch
              variant="label"
              label={t('white')}
              checked={lightSwitch}
              onChange={handleLightSwitch}
            >
              <span className="text-uppercase ms-2">
                {t(lightSwitch ? 'off' : 'on')}
              </span>
            </ToggleSwitch>
          )}
        </div>
        <div className="d-flex flex-column align-items-end text-end gap-2">
          <div onClick={() => !sunSwitch && navigate('/schedule')}>
            <span className="text-uppercase me-2">{t('clock')}</span>
            <Button className="btn-circle p-2" disabled={sunSwitch}>
              <IconSVG
                name="clock"
                color={sunSwitch ? 'grey' : scheduleSwitch ? 'orange' : 'black'}
              />
            </Button>
          </div>
          <div onClick={handleSunSwitch}>
            <div className="d-flex align-items-center">
              <span className="text-uppercase me-2">{t('auto')}</span>
              <Button className="btn-circle p-2">
                <IconSVG name="sun" color={sunSwitch ? 'orange' : 'black'} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Pole Section */}
      <div className="pole-bg position-relative d-flex justify-content-center align-items-center">
        <div
          className="pole-light w-100"
          style={{ background: rgbSwitch ? colorSlider : 'transparent' }}
        />
      </div>
      <ColorPicker color={colorSlider} onChange={handleColorSlider} />
    </>
  );
};

export default LightsPage;
